// @flow
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import _find from 'lodash/find';
import { renderImageMeta } from '../../core/render/visualUtils';
import type { Variant } from '../../library/model/Variant.model';
import type { Cut } from '../../print/model/Cut.model';
import CutStatusWidget from '../../print/CutStatusWidget';
import type { Brand } from '../../library/model/Brand.model';
import type { Device } from '../../library/model/Device.model';
import LoadFailed from '../../elements/LoadFailed';
import ErrorCapsule from '../../core/exceptions/ErrorCapsule';

export type CutPageViewProps = {
  brand: Brand,
  device: Device,
  variants: Array<Variant>, // Never empty
  selectedVariantId: string, // Never empty, neither wrong
  canPrint: boolean,
  currentCut: ?Cut,
  onPrint: (variantId: string) => void,
  onSelectVariant: (id: string) => void,
}

/**
 * Строит URL превью нужного размера из CDN fit-URL.
 * Подменяет '.fit:N:N.' в URL на нужный размер.
 *
 * fit:100:100.jpg -> fit:300:300.jpg
 *
 * Файлы 300:300 прогреты на CDN через GraphQL listVariants заранее.
 * При добавлении новых вариантов прогрев нужно повторить
 * (см. /etc/cron.daily/warmup-previews).
 */
function buildPreviewUrl(preview, size) {
  if (!preview || !preview.fit || !preview.fit.url) return null;
  return preview.fit.url.replace(/\.fit:\d+:\d+\./, `.fit:${size}:${size}.`);
}

export default function CutPageWorkView({
  brand,
  device,
  variants,
  selectedVariantId,
  canPrint,
  currentCut,
  onPrint,
  onSelectVariant,
}: CutPageViewProps) {
  const { t } = useTranslation();
  const [showPreview, setShowPreview] = useState(false);
  const [previewLoaded, setPreviewLoaded] = useState(false);
  const [previewSize, setPreviewSize] = useState(300);  // фоллбэк на 100 при ошибке
  const [pendingVariantId, setPendingVariantId] = useState<?string>(null);
  const [modalReselectEnabled, setModalReselectEnabled] = useState(
    () => localStorage.getItem('modalReselectEnabled') !== 'false'
  );
  const [skipModalEnabled, setSkipModalEnabled] = useState(
    () => localStorage.getItem('skipModalEnabled') === 'true'
  );

  useEffect(() => {
    localStorage.setItem('modalReselectEnabled', String(modalReselectEnabled));
  }, [modalReselectEnabled]);

  useEffect(() => {
    localStorage.setItem('skipModalEnabled', String(skipModalEnabled));
  }, [skipModalEnabled]);

  // Сброс размера превью при смене варианта в модалке
  useEffect(() => {
    if (pendingVariantId !== null) {
      setPreviewSize(300);
      setPreviewLoaded(false);
    }
  }, [pendingVariantId]);

  const activeVariantId = pendingVariantId !== null ? pendingVariantId : selectedVariantId;
  const activeVariant: Variant = _find(variants, ({ id }) => id === activeVariantId);

  function handleCutClick() {
    if (skipModalEnabled) {
      handleConfirmCut();
      return;
    }
    setPreviewLoaded(false);
    setPreviewSize(300);
    setPendingVariantId(selectedVariantId);
    setShowPreview(true);
  }

  function handleConfirmCut() {
    const toPrint = pendingVariantId !== null ? pendingVariantId : selectedVariantId;
    setShowPreview(false);
    setPendingVariantId(null);
    onPrint(toPrint);
  }

  function handleClosePreview() {
    setShowPreview(false);
    setPendingVariantId(null);
  }

  function handleModalReselect(newVariantId: string) {
    setPendingVariantId(newVariantId);
  }

  function handleImageError() {
    // Если 300 не загрузился — пробуем 100, который точно работает
    if (previewSize === 300) {
      setPreviewSize(100);
      setPreviewLoaded(false);
    } else {
      setPreviewLoaded(true);     // и 100 не загрузился — отдаём управление, покажем placeholder
    }
  }

  // URL большого превью генерится только когда модалка открыта
  const largePreviewUrl = showPreview
    ? buildPreviewUrl(activeVariant.preview, previewSize)
    : null;

  return (
    <div className="cut">
      <nav aria-label="breadcrumb">
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <Link to="/frontdesk/">Бренды</Link>
          </li>
          <li className="breadcrumb-item">
            <Link to={`/frontdesk/${brand.id}/`}>{brand.name}</Link>
          </li>
          <li className="breadcrumb-item active" aria-current="page">{device.name}</li>
        </ol>
      </nav>

      <div className="row">
        <div className="col-6">
          <h2 className="mb-3">Выберите вариант резки</h2>
          <div className="row">
            <div className="col-7">
              <div
                className="nav flex-column nav-pills"
                role="tablist"
                aria-orientation="vertical"
              >
                {variants.map(({
                  id,
                  name,
                }) => (
                  <a
                    key={id}
                    className={'nav-link ' + (id === selectedVariantId ? 'active' : '')}
                    href={`#${id}`}
                    role="tab"
                    aria-selected={id === selectedVariantId}
                    onClick={e => onSelectVariant(id)}
                  >
                    {name}
                  </a>
                ))}
              </div>
            </div>
            <div className="col-5">
              {activeVariant.preview ? renderImageMeta(activeVariant.preview.fit) : null}
            </div>
          </div>
        </div>
        <div className="col-6 border-left">
          <h2 className="mb-3">Следуйте шагам</h2>
          <ol>
            <li>Выставить прижимные ролики согласно разметке.</li>
            <li>Вставить лист в плоттер, опустить рычаг и нажать на панели на кнопку 2.</li>
            <li>Нажать на кнопку &quot;Запустить рез&quot;, подтвердить в окне предпросмотра.</li>
          </ol>
          {canPrint ? (
            <div className="d-flex align-items-center mb-4">
              <button type="button" className="btn btn-lg btn-primary" onClick={handleCutClick}>
                Запустить рез
              </button>
              {currentCut && (
                <div className="ml-3">
                  {(currentCut instanceof ErrorCapsule)
                    ? <LoadFailed error={currentCut} />
                    : <CutStatusWidget id={currentCut.id} />
                  }
                </div>
              )}
            </div>
          ) : (
            <div className="alert alert-danger">Печать невозможна. Подключите плоттер.</div>
          )}

          <h2 className="mb-3 mt-4">Настройки</h2>
          <div className="custom-control custom-checkbox mb-2">
            <input
              type="checkbox"
              className="custom-control-input"
              id="skipModalToggle"
              checked={skipModalEnabled}
              onChange={e => setSkipModalEnabled(e.target.checked)}
            />
            <label className="custom-control-label" htmlFor="skipModalToggle" style={{ cursor: 'pointer' }}>
              Отключить превью
            </label>
          </div>
          <div className="custom-control custom-checkbox mb-2">
            <input
              type="checkbox"
              className="custom-control-input"
              id="modalReselectToggle"
              checked={modalReselectEnabled}
              onChange={e => setModalReselectEnabled(e.target.checked)}
            />
            <label className="custom-control-label" htmlFor="modalReselectToggle" style={{ cursor: 'pointer' }}>
              Включить варианты
            </label>
          </div>
        </div>
      </div>

      {/* Компактная модалка предпросмотра */}
      {showPreview && (
        <>
          <div
            className="modal fade show d-block"
            tabIndex="-1"
            role="dialog"
            onClick={handleClosePreview}
          >
            <div
              className="modal-dialog modal-dialog-centered"
              role="document"
              onClick={e => e.stopPropagation()}
              style={{ maxWidth: '680px' }}
            >
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Выберите лекало</h5>
                  <button
                    type="button"
                    className="close"
                    onClick={handleClosePreview}
                  >
                    <span aria-hidden="true">&times;</span>
                  </button>
                </div>
                <div className="modal-body d-flex p-0" style={{ minHeight: '520px' }}>
                  {modalReselectEnabled && (
                    <div
                      className="search-device__dropdown-wrapper"
                      style={{
                        position: 'static',
                        flex: '0 0 220px',
                        maxHeight: '520px',
                        overflowY: 'auto',
                        borderRight: '1px solid #E2E8F0',
                        borderBottom: 'none',
                      }}
                    >
                      {variants.map(({ id, name, preview }) => (
                        <div
                          key={id}
                          className="d-flex align-items-center"
                          style={{
                            borderBottom: '1px solid #EEF2F6',
                            display: 'flex',
                            gap: '0.625rem',
                            cursor: 'pointer',
                            background: id === activeVariantId ? '#EEF2FF' : 'transparent',
                            color: id === activeVariantId ? '#4338CA' : '#0F172A',
                            padding: '0.625rem 0.875rem',
                          }}
                          onClick={() => handleModalReselect(id)}
                        >
                          <span
                            style={{
                              display: 'inline-block',
                              width: '36px',
                              height: '36px',
                              backgroundColor: '#F6F7F9',
                              backgroundSize: 'contain',
                              backgroundPosition: 'center',
                              backgroundRepeat: 'no-repeat',
                              backgroundImage: preview && preview.fit ? `url(${preview.fit.url})` : null,
                              borderRadius: '6px',
                              flexShrink: 0,
                            }}
                          />
                          <span style={{ fontWeight: id === activeVariantId ? 500 : 400, fontSize: '0.875rem' }}>
                            {name}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="d-flex flex-column align-items-center justify-content-center flex-grow-1 p-3" style={{ minHeight: '520px' }}>
                    {largePreviewUrl ? (
                      <>
                        {!previewLoaded && (
                          <div className="spinner-border text-primary" role="status">
                            <span className="sr-only">Загрузка…</span>
                          </div>
                        )}
                        <img
                          key={previewSize}
                          src={largePreviewUrl}
                          alt={activeVariant.name}
                          onLoad={() => setPreviewLoaded(true)}
                          onError={handleImageError}
                          style={{
                            maxWidth: '100%',
                            maxHeight: '360px',
                            width: 'auto',
                            height: 'auto',
                            objectFit: 'contain',
                            display: previewLoaded ? 'block' : 'none',
                          }}
                        />
                      </>
                    ) : (
                      <p className="text-muted m-0">Предпросмотр недоступен</p>
                    )}
                    <div className="d-flex justify-content-end align-items-center w-100 mt-auto pt-3" style={{ gap: '0.5rem', borderTop: '1px solid #E2E8F0', flexShrink: 0 }}>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={handleClosePreview}
                      >
                        Отмена
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleConfirmCut}
                      >
                        Запустить рез
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" />
        </>
      )}
    </div>
  );
}